import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { WEBSITE_PAGES_LOADING } from "../../constants/loading.constant";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useWebsitePageCore } from "../../cores/websitePage";
import { WebsitePage } from "../../cores/websitePage/interfaces/WebsitePage";
import { websitePageApiService } from "../../cores/websitePage/services/api";
import WaitingGameOffer from "../component/games/WaitingGameOffer";
import { CreatePageModal, CreatePagePayload } from "../component/website/CreatePageModal";
import { MiniPagePreview } from "../component/website/MiniPagePreview";
import { WebsiteIcon } from "../component/website/WebsiteIcon";
import { WebsiteModuleIcon } from "../component/website/WebsiteModuleIcon";
import { getPublishedPageUrl } from "../component/website/websitePageUrls";
import { AiWebsiteFormData } from "../component/website/CreateAiWebsiteModal";
import { UpgradePlanButton } from "../component/plan/UpgradePlanButton";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "./WebsitePages.scss";
import "../component/website/website-builder.scss";
import "../component/website/WebsiteModuleIcon.scss";

type StatusFilter = "all" | "draft" | "published";

export default function WebsitePages() {
  const intl = useIntl();
  const { company, companyRole, isAdmin, isReseller, companyPlan } = useMeCore();
  const { goToEditWebsitePage, goToSocial } = useRouterCore();
  const { pages, getPages, createPage, deletePage, generateAi } = useWebsitePageCore();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);

  useEffect(() => {
    if (
      companyPlan?.rules.websitePagesAmount === 0 ||
      (!companyRole?.forms &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    ) {
      goToSocial();
    }
  }, [companyPlan, companyRole, goToSocial, isAdmin, isReseller]);

  useEffect(() => {
    if (company?.id) getPages(company.id);
  }, [company, getPages]);

  const filteredPages = useMemo(() => {
    const query = search.trim().toLowerCase();
    return pages.filter((page) => {
      const matchesSearch = !query || page.name.toLowerCase().includes(query) || page.slug.includes(query);
      const matchesStatus = statusFilter === "all" || page.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [pages, search, statusFilter]);

  const stats = useMemo(
    () => ({
      total: pages.length,
      published: pages.filter((p) => p.status === "published").length,
      draft: pages.filter((p) => p.status === "draft").length,
    }),
    [pages]
  );

  const handleCreate = useCallback(
    async (payload: CreatePagePayload) => {
      const page = await createPage(company!.id, payload);
      goToEditWebsitePage(page.id);
    },
    [company, createPage, goToEditWebsitePage]
  );

  const handleAiGenerate = useCallback(
    async (pageName: string, form: AiWebsiteFormData) => {
      if (!company?.id || isAiGenerating) return;

      setIsAiGenerating(true);
      try {
        let logoUrl: string | undefined;
        const imageUrls: string[] = [];

        if (form.logoFile) {
          const uploaded = await websitePageApiService.uploadImage(company.id, form.logoFile);
          logoUrl = uploaded.url;
        }

        for (const file of form.imageFiles.slice(0, 6)) {
          const uploaded = await websitePageApiService.uploadImage(company.id, file);
          imageUrls.push(uploaded.url);
        }

        const content = await generateAi(company.id, {
          companyName: form.companyName,
          description: form.description,
          designPreferences: form.designPreferences,
          logoUrl,
          imageUrls,
          brandColors: form.brandColors,
        });

        const page = await createPage(company.id, {
          name: pageName,
          templateId: "custom-ai",
          contentJson: content,
        });

        setShowCreateModal(false);
        goToEditWebsitePage(page.id);
      } catch {
        // Errors are surfaced by generateAi / createPage.
      } finally {
        setIsAiGenerating(false);
      }
    },
    [company, createPage, generateAi, goToEditWebsitePage, isAiGenerating]
  );

  const handleDelete = useCallback(
    async (page: WebsitePage) => {
      if (window.confirm(intl.formatMessage({ id: "app.websitePage.confirmDelete" }))) {
        await deletePage(company!.id, page.id);
      }
    },
    [company, deletePage, intl]
  );

  const canAccess =
    companyRole?.forms || companyRole?.owner || companyRole?.admin || companyRole?.agent || isAdmin || isReseller;

  const canCreate = useMemo(
    () => companyPlan && pages.length < (companyPlan?.rules.websitePagesAmount || 0),
    [companyPlan, pages.length]
  );

  const formatDate = (date: string | null) => {
    if (!date) return "—";
    return new Intl.DateTimeFormat(intl.locale, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(date)
    );
  };

  const filters: { id: StatusFilter; label: string; count?: number }[] = [
    { id: "all", label: intl.formatMessage({ id: "app.websitePage.filter.all" }), count: stats.total },
    { id: "published", label: intl.formatMessage({ id: "app.websitePage.filter.published" }), count: stats.published },
    { id: "draft", label: intl.formatMessage({ id: "app.websitePage.filter.draft" }), count: stats.draft },
  ];

  return (
    <Layout>
      <Content>
        <UpdateLoadingCard loadingId={WEBSITE_PAGES_LOADING} />
        {isAiGenerating && (
          <div className="wp-pages__ai-banner" role="status" aria-live="polite">
            <WebsiteIcon icon="circle-notch" spin />
            <span>{intl.formatMessage({ id: "app.websitePage.createAi.backgroundNotice" })}</span>
          </div>
        )}
        <div className="wp-builder wp-pages">
          <div className="wp-pages__hero">
            <div className="wp-pages__hero-glow" />
            <div className="wp-pages__hero-content">
              <div className="wp-pages__hero-text">
                <WebsiteModuleIcon />
                <div>
                  <h1 className="wp-pages__title">{intl.formatMessage({ id: "app.websitePage.title" })}</h1>
                  <p className="wp-pages__subtitle">{intl.formatMessage({ id: "app.websitePage.subtitle" })}</p>
                </div>
              </div>
              <div className="wp-pages__hero-actions">
                {canAccess && canCreate && (
                  <button type="button" className="wp-btn wp-btn--primary" onClick={() => setShowCreateModal(true)}>
                    <WebsiteIcon icon="plus" />
                    {intl.formatMessage({ id: "app.websitePage.createButton" })}
                  </button>
                )}
                {canAccess && !canCreate && <UpgradePlanButton />}
              </div>
            </div>
            <div className="wp-pages__stats">
              <div className="wp-pages__stat">
                <span className="wp-pages__stat-value">{stats.total}</span>
                <span className="wp-pages__stat-label">{intl.formatMessage({ id: "app.websitePage.stats.total" })}</span>
              </div>
              <div className="wp-pages__stat">
                <span className="wp-pages__stat-value">{stats.published}</span>
                <span className="wp-pages__stat-label">{intl.formatMessage({ id: "app.websitePage.stats.published" })}</span>
              </div>
              <div className="wp-pages__stat">
                <span className="wp-pages__stat-value">{stats.draft}</span>
                <span className="wp-pages__stat-label">{intl.formatMessage({ id: "app.websitePage.stats.draft" })}</span>
              </div>
              {companyPlan && (
                <div className="wp-pages__stat wp-pages__stat--limit">
                  <span className="wp-pages__stat-value">
                    {pages.length}/{companyPlan.rules.websitePagesAmount}
                  </span>
                  <span className="wp-pages__stat-label">{intl.formatMessage({ id: "app.websitePage.stats.limit" })}</span>
                </div>
              )}
            </div>
          </div>

          <div className="wp-pages__toolbar">
            <div className="wp-pages__search">
              <WebsiteIcon icon="search" className="wp-pages__search-icon" />
              <input
                className="wp-input"
                type="search"
                placeholder={intl.formatMessage({ id: "app.websitePage.search" })}
                aria-label={intl.formatMessage({ id: "app.websitePage.search" })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="wp-pages__filters">
              {filters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`wp-pages__filter ${statusFilter === f.id ? "wp-pages__filter--active" : ""}`}
                  aria-pressed={statusFilter === f.id}
                  onClick={() => setStatusFilter(f.id)}
                >
                  {f.label}
                  {f.count !== undefined && <span className="wp-pages__filter-count">{f.count}</span>}
                </button>
              ))}
            </div>
          </div>

          {filteredPages.length > 0 ? (
            <div className="wp-pages__grid">
              {filteredPages.map((page) => (
                <article key={page.id} className="wp-pages__card">
                  <div
                    className="wp-pages__card-preview"
                    role="button"
                    tabIndex={0}
                    aria-label={intl.formatMessage(
                      { id: "app.websitePage.card.openEditor" },
                      { name: page.name }
                    )}
                    onClick={() => goToEditWebsitePage(page.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToEditWebsitePage(page.id);
                      }
                    }}
                  >
                    <MiniPagePreview content={page.contentJson} />
                    <div className="wp-pages__card-overlay">
                      <span className="wp-btn wp-btn--secondary wp-btn--sm">
                        <WebsiteIcon icon="pen" />
                        {intl.formatMessage({ id: "app.websitePage.card.edit" })}
                      </span>
                    </div>
                  </div>
                  <div className="wp-pages__card-body">
                    <div className="wp-pages__card-top">
                      <h3 className="wp-pages__card-title">{page.name}</h3>
                      <span className={`wp-badge wp-badge--${page.status}`}>
                        <span className="wp-pages__status-dot" />
                        {page.status === "published"
                          ? intl.formatMessage({ id: "app.websitePage.status.published" })
                          : intl.formatMessage({ id: "app.websitePage.status.draft" })}
                      </span>
                    </div>
                    <p className="wp-pages__card-slug">/{page.slug}</p>
                    <p className="wp-pages__card-date">
                      <WebsiteIcon icon="clock" />
                      {page.status === "published" && page.publishedAt
                        ? intl.formatMessage({ id: "app.websitePage.card.publishedAt" }, { date: formatDate(page.publishedAt) })
                        : intl.formatMessage({ id: "app.websitePage.card.updatedAt" }, { date: formatDate(page.updatedAt) })}
                    </p>
                  </div>
                  <div className="wp-pages__card-actions">
                    <button
                      type="button"
                      className="wp-btn wp-btn--secondary wp-btn--sm"
                      onClick={() => goToEditWebsitePage(page.id)}
                    >
                      <WebsiteIcon icon="pen" />
                      {intl.formatMessage({ id: "app.websitePage.card.edit" })}
                    </button>
                    {page.status === "published" && (
                      <button
                        type="button"
                        className="wp-btn wp-btn--ghost wp-btn--sm"
                        onClick={() =>
                          window.open(
                            page.publicUrl.startsWith("http")
                              ? page.publicUrl
                              : getPublishedPageUrl(page.companyId, page.slug),
                            "_blank"
                          )
                        }
                      >
                      <WebsiteIcon icon="external-link-alt" />
                        {intl.formatMessage({ id: "app.websitePage.card.view" })}
                      </button>
                    )}
                    <button
                      type="button"
                      className="wp-btn wp-btn--danger wp-btn--sm"
                      onClick={() => handleDelete(page)}
                    >
                      <WebsiteIcon icon="trash-alt" />
                      {intl.formatMessage({ id: "app.websitePage.card.delete" })}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="wp-pages__empty">
              <div className="wp-pages__empty-icon">
                <WebsiteModuleIcon />
              </div>
              <h3>{intl.formatMessage({ id: "app.websitePage.emptyTitle" })}</h3>
              <p>{intl.formatMessage({ id: "app.websitePage.empty" })}</p>
              {canAccess && canCreate && (
                <button type="button" className="wp-btn wp-btn--primary" onClick={() => setShowCreateModal(true)}>
                  <WebsiteIcon icon="plus" />
                  {intl.formatMessage({ id: "app.websitePage.createButton" })}
                </button>
              )}
            </div>
          )}
        </div>

        <CreatePageModal
          isOpen={showCreateModal}
          isAiGenerating={isAiGenerating}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
          onAiGenerate={handleAiGenerate}
        />
        <WaitingGameOffer
          isWaiting={isAiGenerating}
          promptTitle={intl.formatMessage({ id: "app.websitePage.createAi.dinoPromptTitle" })}
          promptLead={intl.formatMessage({ id: "app.websitePage.createAi.dinoPromptLead" })}
          promptQuestion={intl.formatMessage({ id: "app.websitePage.createAi.dinoPromptQuestion" })}
          gameNote={intl.formatMessage({ id: "app.websitePage.createAi.dinoGameNote" })}
          closeButtonLabel={intl.formatMessage({ id: "app.websitePage.createAi.dinoCloseGame" })}
        />
      </Content>
    </Layout>
  );
}
